import React, { useState, useEffect, Fragment } from 'react';
import { 
  Box, 
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OrderBookMobile from './OrderBookMobile';
import ButtonGroup from '@mui/material/ButtonGroup';
import RangeSlider from './AmountRange';
import Chip from '@mui/material/Chip';
import CoinSlide from './CoinSlide';
import Limit from './Forms/Limit';
import Conditional from './Forms/Conditional';
import Iceberg from './Forms/Iceberg';
import Market from './Forms/Market';
import Oco from './Forms/Oco';
import ScaledOrder from './Forms/ScaledOrder';
import TpSl from './Forms/TpSl';
import TrailingStop from './Forms/TrailingStop';
import Twap from './Forms/Twap';
import { useSelector } from 'react-redux';
import TradeHistory from '../Trader/TradeHistory';


const DarkContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  minHeight: '100vh',
  width: '100%',
  overflow: 'hidden',
  position: 'relative'
}));

const TopNav = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1),
  backgroundColor: '#1a1a1a',
  borderBottom: '1px solid #2a2a2a'
}));

const NavItem = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  minWidth: 'auto',
  textTransform: 'none'
}));





const BybitTrade = () => {
  const currentPair = useSelector((state) => state.currentPair.currentPair);
  const tradingPlans = useSelector((state) => state.plans.tradingPlans);
  const coinRates = useSelector((state) => state.coins.coinRates);
  const stocks = useSelector((state) => state.coins.stock);
  const pairType = useSelector((state) => state.currentPair.pairType);

  const fundings = useSelector((state) => state.wallet.fundings);
  const tradings = useSelector((state) => state.history.tradings);
  const investments = useSelector((state) => state.history.investments);
  const withdrawals = useSelector((state) => state.history.withdrawals);
  const fundingBal = useSelector((state) => state.wallet.fundingBalance);


  const formatPrice = (amount)=> new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount);

  const [activeTab, setActiveTab] = useState('Trade');
  const [available, setAvailable] = useState('Limit');
  const [coinDetails, setCoinDetails ] = useState(null);
  const [orderType, setOrderType] = useState('Buy');


  const calculate24hrChange = (coin) => {
    let pair = {};
    if (pairType === 'stocks') {
      stocks.forEach(stock => {
        if (stock.short_name === coin) {
          pair = stock;
        }
      });
    }else{
      coinRates.forEach(coinRater => {
        if (coinRater.symbol === coin) {
          pair = coinRater;
        }
      });
    }
   
  return pair;
  }

  const calculateBalance = (coin) => {
    let amount = 0;

    fundings.forEach(fund => {
      if (fund.gateway === coin) {
        amount += parseFloat(fund.amount);
      }
      });

      tradings.forEach(trade => {
        if (trade.pair === coin) {
          amount += parseFloat(trade.profit);
        }
        });

        investments.forEach(investment => {
          if (investment.payment_source === coin && investment.status != 'declined') {
            amount -= parseFloat(investment.amount_invested);
          }
          });

          withdrawals.forEach(withdrawal => {
            
            if (withdrawal.source === coin && withdrawal.status != 'declined') {
              amount -= parseFloat(withdrawal.amount);
            }
            });
    
      return amount;
  }

  useEffect(() => {
    setCoinDetails(calculate24hrChange(currentPair))    
    }, []);

  return (
    <DarkContainer>
    
    {coinDetails !== null &&
    <Fragment>
     
      <Grid container> {/* Adjust height for nav + tabs */}
        {/* Left Side - Trading Form */}
        <Grid item xs={7} sm={7} 
          sx={{ 
            borderRight: '1px solid #2a2a2a',
            height: '100%',
            overflow: 'auto'
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Trading Pair Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='h6'> {`${currentPair}`}</Typography>
                <Typography sx={{ fontSize: '14px', color: `${parseFloat(coinDetails?.changePercent24Hr || coinDetails?.change_price) >= 0 ? '#4caf50': '#f31348'}`, ml: 1 }}>
               {parseFloat(coinDetails?.changePercent24Hr || coinDetails?.change_price) > 0 && '+'} {parseFloat(coinDetails?.changePercent24Hr || coinDetails?.change_price).toFixed(4)}
                </Typography>
              </Box>             
            </Box>
            <Box mt={-2} mb={2} sx={{textAlign: 'left'}}>
              <Typography variant='h6'>
                {formatPrice(parseFloat(coinDetails.priceUsd).toFixed(6))}
              </Typography>
            </Box>
            <Box sx={{marginBottom: '6px'}}>
              <ButtonGroup size='small' disableElevation='true'  sx={{padingX: '25px'}} variant="contained" aria-label="Basic button group">
                <Button onClick={()=>setOrderType('Buy')} color={orderType === 'Buy' ? 'success' : 'inherit'} sx={{ width: '95px', color: '#fff'}}>Buy</Button>

                <Button onClick={()=>setOrderType('Sell')} color={orderType === 'Sell' ? 'error' : 'inherit'} sx={{ width: '95px', color: '#fff'}}>Sell</Button>
              </ButtonGroup>
              </Box>
            {/* Trading Controls */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">Available</Typography>
              <Typography variant="subtitle2">{formatPrice(parseFloat(fundingBal))}</Typography>
            </Box>

            <Select
              fullWidth
              size="small"
              defaultValue={'Limit'}
              onChange={(e)=>setAvailable(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select Option</MenuItem>
              <MenuItem value="Limit">Limit</MenuItem>
              <MenuItem value="Market">Market</MenuItem>
              <MenuItem value="TP/SL">TP/SL</MenuItem>
              <MenuItem value="Conditional">Conditional</MenuItem>
              <MenuItem value="OCO">OCO</MenuItem>
            </Select>

{/* different forms starts here */}
              {available === null && <Limit coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)} />}
              {available === 'Limit' && <Limit coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)}/>}
              {available === 'Market' && <Market coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)}/>}
              {available === 'TP/SL' && <TpSl coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)}/>}
              {available === 'Conditional' && <Conditional coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)}  orderType={orderType} balance={parseFloat(fundingBal)}/>}
              {available === 'OCO' && <Oco orderType={orderType} coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} balance={parseFloat(fundingBal)}/>}
              {available === 'Trailing Stop' && <TrailingStop coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)}/>}
              {available === 'Iceberg' && <Iceberg coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)}/>}
              {available === 'TWAP' && <Twap coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)}/>}
              {available === 'Scaled Order' && <ScaledOrder coinPrice={parseFloat(coinDetails.priceUsd).toFixed(6)} orderType={orderType} balance={parseFloat(fundingBal)}/>}


          </Box>
        </Grid>

        {/* Right Side - Order Book */}
        <Grid item xs={5} sm={5} 
          sx={{ 
            height: '100%',
            overflow: 'auto'
          }}
        >
          <Box sx={{ p: 1 }}>
           
          <OrderBookMobile price={parseFloat(coinDetails.priceUsd)}/>
           
          </Box>
        </Grid>
      </Grid>
    <CoinSlide/>
    </Fragment>
        }
    </DarkContainer>
  );
};

export default BybitTrade;
